import React, { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Switch, Tooltip } from "antd";
import {
  CheckCircleFilled,
  DeleteFilled,
  DownloadOutlined,
  PlusOutlined,
  HomeOutlined,
  EyeOutlined,
  EditFilled,
  MedicineBoxOutlined
} from "@ant-design/icons";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { Button, Input, Pagination, Space, Table, Tag } from "antd";


import { useGetMedicationScheduleQuery, useMarkTakenMutation } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";


const MedicationList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialMedicationDetail = location.state?.medicationDetail || null;

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const currentDate = new Date();
  const firstDayOfMonth = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
  const lastDayOfMonth = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));

  const [startDate, setStartDate] = useState(initialMedicationDetail?.intakeDate || firstDayOfMonth);
  const [endDate, setEndDate] = useState(initialMedicationDetail?.intakeDate || lastDayOfMonth);
  const [searchText, setSearchText] = useState(initialMedicationDetail?.medicationName || "");
  const [activeMedicationDetail, setActiveMedicationDetail] = useState<any>(initialMedicationDetail);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const userData = useEffectiveUserData();
  const patientId = userData?.patientId || 14;

  const { data: medicationApiData, isLoading } =
    useGetMedicationScheduleQuery(
      { patientId, startDate, endDate },
      { skip: !patientId }
    );

 const tableData = useMemo(() => {
  return (
    medicationApiData?.data?.medicationSchedule
      ?.filter((schedule: any) => schedule.medications?.length > 0)
      ?.flatMap((schedule: any) =>
        schedule.medications.map((med: any, index: number) => {
          const intake = med.intakeTime || {};

          const times: any[] = [];

          if (intake.morning) times.push(`Morning ${intake.morning.time}`);
          if (intake.noon) times.push(`Noon ${intake.noon.time}`);
          if (intake.evening) times.push(`Evening ${intake.evening.time}`);
          if (intake.night) times.push(`Night ${intake.night.time}`);

          return {
            key: `${schedule.startDate}-${schedule.endDate}-${med.patientMedicationId}-${index}`,
            medicationId: med.medicationId || med.patientMedicationId,
            medicationName: med.medicationName,
            drugIntakeProgressId: med.patientMedicationId,
            type: med.medicationType || "tablet",

            cause:
              intake.morning?.mealInstruction ||
              intake.noon?.mealInstruction ||
              intake.evening?.mealInstruction ||
              intake.night?.mealInstruction ||
              "-",

            dosage:
              intake.morning?.dosage ||
              intake.noon?.dosage ||
              intake.evening?.dosage ||
              intake.night?.dosage ||
              "-",

            frequency: `${med.frequencyPerDay} times a day`,
            times,
            rawTimes: times,

            routine:
              intake.morning?.mealInstruction ||
              intake.noon?.mealInstruction ||
              intake.evening?.mealInstruction ||
              intake.night?.mealInstruction ||
              "-",

            duration: `${schedule.startDate} - ${schedule.endDate}`,
            dateDetail: `Start: ${schedule.startDate}`,
            status: "Active",
          };
        })
      ) || []
  );
}, [medicationApiData]);

const filteredData = useMemo(() => {
  let rows = tableData;

  // 🔹 medication detail filter
  if (activeMedicationDetail) {
    rows = rows.filter((row: any) => {
      return (
        (!activeMedicationDetail.medicationId ||
          String(row.medicationId) === String(activeMedicationDetail.medicationId)) &&

        (!activeMedicationDetail.medicationName ||
          row.medicationName?.toLowerCase().includes(activeMedicationDetail.medicationName.toLowerCase())) &&

        (!activeMedicationDetail.dosage ||
          String(row.dosage) === String(activeMedicationDetail.dosage)) &&

        (!activeMedicationDetail.scheduledTime ||
          row.rawTimes?.some((time: string) =>
            time.toLowerCase().includes(
              String(activeMedicationDetail.scheduledTime).toLowerCase()
            )
          ))
      );
    });
  }

  // 🔹 search filter
  const search = searchText.trim().toLowerCase();
  if (!search) return rows;

  return rows.filter((row: any) => {
    return (
      row.medicationName?.toLowerCase().includes(search) ||
      row.type?.toLowerCase().includes(search) ||
      row.dosage?.toString().toLowerCase().includes(search) ||
      row.frequency?.toLowerCase().includes(search) ||
      row.routine?.toLowerCase().includes(search) ||
      row.cause?.toLowerCase().includes(search) ||
      row.duration?.toLowerCase().includes(search) ||
      row.status?.toLowerCase().includes(search) ||
      row.dateDetail?.toLowerCase().includes(search) ||
      row.rawTimes?.some((t: string) => t.toLowerCase().includes(search))
    );
  });
}, [tableData, searchText, activeMedicationDetail]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, startDate, endDate, activeMedicationDetail]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredData.length, pageSize, currentPage]);

  const [checked, setChecked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [submit] = useMarkTakenMutation();

  const handleToggle = (value: boolean, record: any) => {
    if (value) {
      setSelectedRow(record);
      setIsModalOpen(true);
    } else {
      setChecked(false);
    }
  };
  const handleConfirm = async () => {
    try {
      await submit({
        patientId: patientId,
        drugIntakeProgressId: selectedRow?.drugIntakeProgressId,
      }).unwrap();

      setChecked(true);
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = () => {
    setChecked(false);
    setIsModalOpen(false);
  };

  const columns = [
    {
      title: "Medication Name",
      dataIndex: "medicationName",
      key: "medicationName",
      width: 200,
      ellipsis: true,
      render: (text: any, record: any) => (
        <Tooltip title={text}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                backgroundColor: record.type === "syrup" ? "#fff7e6" : "#e0e5f9",
                padding: "8px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MedicineBoxOutlined
                style={{
                  color: record.type === "syrup" ? "#ffa940" : "#5c67d2",
                  fontSize: "18px",
                }}
              />
            </div>

            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontWeight: "bold",
                  color: "#1e293b",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {text}
              </div>

              <div
                style={{
                  fontSize: "12px",
                  color: "#94a3b8",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Cause: {record.cause}
              </div>
            </div>
          </div>
        </Tooltip>
      ),
    },

    {
      title: "Dosage / Quantity",
      dataIndex: "dosage",
      key: "dosage",
      width: 120,
      ellipsis: true,
    },

    {
  title: "Frequency & Time",
  dataIndex: "frequency",
  key: "frequency",
  width: 220,
  render: (text: any, record: any) => (
    <Tooltip title={record.times.join(", ")}>
      <div style={{ width: "100%" }}> {/* Container width ensure wrap works */}
        <div style={{ marginBottom: "4px" }}>{text}</div>
        <Space 
          size={[4, 8]} // [horizontal, vertical] gap
          wrap          // This makes the tags go to the next line
          style={{ width: '100%' }}
        >
          {record.times.map((time: any) => (
            <Tag
              key={time}
              style={{
                backgroundColor: "#dbeafe",
                color: "#5c67d2",
                border: "none",
                borderRadius: "4px",
                fontSize: "11px",
                margin: 0 // Space component handles margins
              }}
            >
              {time}
            </Tag>
          ))}
        </Space>
      </div>
    </Tooltip>
  )
},

    {
      title: "Routine",
      dataIndex: "routine",
      key: "routine",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text: any) => (
        <Tooltip title={text}>
          <span style={{ color: "#64748b" }}>{text}</span>
        </Tooltip>
      ),
    },

    {
      title: "From Date - To Date",
      dataIndex: "duration",
      key: "duration",
      width: 200,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
    },

    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (record: any) => (
        <Space>
          <Button
            type="text"
            shape="circle"
            style={{ background: "#1890ff", color: "#fff" }}
            icon={<EyeOutlined />}
            onClick={() => {
              navigate(`/medication-details`, { state: record });
            }}

          />
          {/* <Button
            type="text"
            shape="circle"
            style={{ background: "#f5435d", color: "#fff" }}
            icon={<DeleteFilled />}
          /> */}
          {/* <Switch
            checked={checked}
            onChange={(value) => handleToggle(value, record)}
          /> */}
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-full table-padding">
        <div className="page-header">
          <div className="breadcrumb">
            <h3 className="page-title">Medication</h3>
            <span className="customize-breadcrumb">
              <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /> </span>
              <span className="separator "> - </span>
              <span className="current">Medication List</span>
            </span>
          </div>
        </div>
        <div className="family-card">

          {/* Header */}
          <div className="family-header">
            {/* LEFT SIDE */}
            <div className="left-side">
              <p className="title">Medication</p>
              <Input.Search
                placeholder="Search..."
                className="search-input"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setActiveMedicationDetail(null);
                }}
                allowClear
              />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActiveMedicationDetail(null);
                }}
                className="form-control"
                style={{ width: "150px" }}
              />

              <input
                type="date"
                min={startDate}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActiveMedicationDetail(null);
                }}
                className="form-control"
                style={{ width: "150px" }}
              />

            </div>

            {/* RIGHT SIDE */}
            <div className="right-side">
              <Space>
                <Button className="btn-add" icon={<PlusOutlined />} onClick={() => navigate("/addEditMedication")} />
                {/* <Button className="btn-download" icon={<DownloadOutlined />} /> */}
              </Space>
            </div>
          </div>


          {/* Table */}
          <Table
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
            className="family-table"
            loading={isLoading}
            scroll={{ y: 400 }}
          />

          {/* Footer */}
          <div className="family-footer">
            <span>Total {filteredData.length} Medications</span>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredData.length}
              showSizeChanger
              pageSizeOptions={["5", "10", "20", "50"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(page, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
            />
          </div>

        </div>
      </div>
      <Modal
        title="Mark Medication as Taken?"
        open={isModalOpen}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Yes"
        cancelText="Cancel"
      >
        Are you sure you want to mark this medication as taken?
      </Modal>
    </div>


  );
};

export default MedicationList;
